import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError, eur } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SinistriDeclaration, SINISTRI_YEARS } from "@/components/SinistriDeclaration";
import { IddDeclaration } from "@/components/IddDeclaration";
import { MrvConfronto } from "@/components/MrvConfronto";
import { Calculator, ShieldCheck, AlertTriangle, XCircle, Wallet, Save, Loader2, Lock, HeartHandshake } from "lucide-react";

export default function MrvQuoteEngine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [anagrafiche, setAnagrafiche] = useState([]);
  const [anagraficaId, setAnagraficaId] = useState(params.get("anagrafica") || "");
  const requestId = params.get("request");
  const anagraficaQs = params.get("anagrafica");
  const editId = params.get("edit");
  const { isAdmin } = useAuth();
  const [editLoaded, setEditLoaded] = useState(false);

  const [opzioneTipo, setOpzioneTipo] = useState("A");
  const [combinazione, setCombinazione] = useState("2");
  const [nVolontari, setNVolontari] = useState("");
  const [dataEffetto, setDataEffetto] = useState(new Date().toISOString().slice(0, 10));
  const [premioScelta, setPremioScelta] = useState("");
  const [premioMagg, setPremioMagg] = useState("");
  const [sinistri, setSinistri] = useState(null);
  const [idd, setIdd] = useState(null);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/mrv/config").then(({ data }) => setConfig(data));
    api.get("/anagrafiche").then(({ data }) => setAnagrafiche(data));
  }, []);

  const labels = config?.garanzia_labels || {};

  useEffect(() => {
    if (!config || !editId || editLoaded) return;
    api.get(`/quotes/${editId}`).then(({ data: q }) => {
      const i = q.mrv_input || {};
      setAnagraficaId(q.anagrafica_id || "");
      if (i.opzione_tipo) setOpzioneTipo(i.opzione_tipo);
      if (i.combinazione) setCombinazione(String(i.combinazione));
      setNVolontari(String(i.n_volontari ?? ""));
      if (i.data_effetto) setDataEffetto(i.data_effetto);
      if (i.premio_scelta) setPremioScelta(i.premio_scelta);
      if (i.premio_maggiorato) setPremioMagg(String(i.premio_maggiorato));
      if (q.sinistri) setSinistri(q.sinistri);
      if (q.idd) setIdd(q.idd);
      setEditLoaded(true);
      toast.info("Modalità modifica: aggiorna i dati e salva per ricalcolare");
    }).catch(() => setEditLoaded(true));
  }, [config, editId, editLoaded]);

  const buildPayload = () => ({
    n_volontari: Number(nVolontari || 0), opzione_tipo: opzioneTipo, combinazione,
    data_effetto: dataEffetto, premio_scelta: premioScelta || null,
    premio_maggiorato: premioScelta === "maggiorato" && premioMagg ? Number(premioMagg) : null, sinistri, idd,
  });

  const calculate = async (scelta, combo) => {
    setCalculating(true);
    try {
      const s = scelta ?? (premioScelta || null);
      const payload = { ...buildPayload(), ...(combo ? { combinazione: combo } : {}), quote_id: editId || null, premio_scelta: s, premio_maggiorato: s === "maggiorato" && premioMagg ? Number(premioMagg) : null };
      const { data } = await api.post("/mrv/quotes/calculate", payload);
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalculating(false);
    }
  };

  const save = async () => {
    if (!anagraficaId) return toast.error("Seleziona un'anagrafica");
    setSaving(true);
    try {
      const { data } = await api.post("/mrv/quotes", { ...buildPayload(), anagrafica_id: anagraficaId, request_id: requestId, quote_id: editId || undefined });
      toast.success(editId ? "Preventivo aggiornato" : "Preventivo salvato");
      navigate(isAdmin ? `/admin/preventivi?open=${data.id}` : `/preventivi/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const chooseScelta = (v) => { setPremioScelta(v); calculate(v); };
  const pickCombo = (c) => { setCombinazione(c); setPremioScelta(""); setPremioMagg(""); calculate("tariffa", c); };
  const combos = (config?.combinazioni || []).filter((c) => !c.riservata || c.code === combinazione);

  useEffect(() => {
    if (!config || editId) return;
    const visible = config.combinazioni.filter((c) => !c.riservata);
    if (visible.length && !visible.some((c) => c.code === combinazione)) setCombinazione(visible[0].code);
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusBanner = useMemo(() => {
    if (!result) return null;
    if (result.status === "non_ammissibile")
      return { icon: XCircle, tone: "bg-red-50 border-red-200 text-red-800", title: "Rischio non assumibile",
        msgs: result.eligibility.blocking_errors };
    if (result.binario === "referral_tecnico")
      return { icon: AlertTriangle, tone: "bg-amber-50 border-amber-200 text-amber-800", title: "Referral tecnico richiesto",
        msgs: [...(result.eligibility.warnings || []), "Emissione autonoma bloccata: invia all'Agenzia."] };
    return { icon: ShieldCheck, tone: "bg-emerald-50 border-emerald-200 text-emerald-800", title: "Preventivo standard — emettibile",
      msgs: result.eligibility.warnings || [] };
  }, [result]);

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  const rcQuery = [anagraficaQs ? `anagrafica=${anagraficaQs}` : "", requestId ? `request=${requestId}` : ""].filter(Boolean).join("&");
  const premium = result?.premium;

  return (
    <div className="space-y-6">
      <div>
        <div className={`mb-3 flex flex-wrap gap-2 ${editId ? "hidden" : ""}`} data-testid="product-switch">
          <Link to={`/quota/nuovo${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-rc"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            RC Economia Solidale
          </Link>
          <span className="flex items-center gap-1.5 rounded-full border border-forest bg-forest px-4 py-1.5 text-sm font-medium text-white">
            <HeartHandshake className="h-4 w-4" /> Tutela Multirischi Volontariato
          </span>
          <Link to={`/quota/infortuni${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-inf"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            Infortuni Economia Solidale
          </Link>
          <Link to={`/quota/catnat${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-catnat"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            CAT NAT Assimoco
          </Link>
          <Link to={`/quota/multirischi${rcQuery ? `?${rcQuery}` : ""}`} data-testid="switch-to-multirischi"
            className="rounded-full border border-border px-4 py-1.5 text-sm font-medium text-forest/70 hover:bg-sage">
            Multirischi Danni ai Beni
          </Link>
        </div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Preventivo — Tutela Multirischi del Volontariato</h1>
        <p className="mt-1 text-muted-foreground">Ed. {config.product_version} · min. {config.min_volontari} volontari · premio minimo di polizza {eur(config.min_premio_polizza)}.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 lg:col-span-3">
          <Card className="rounded-xl border-border/60 p-5">
            <h2 className="mb-4 font-heading text-lg font-semibold text-forest">Dati di rischio</h2>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Anagrafica cliente</Label>
                <Select value={anagraficaId} onValueChange={setAnagraficaId}>
                  <SelectTrigger data-testid="mrv-anagrafica" className="[&>span]:truncate"><SelectValue placeholder="Seleziona ente..." /></SelectTrigger>
                  <SelectContent>{anagrafiche.map((a) => <SelectItem key={a.id} value={a.id}>{a.ragione_sociale}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Opzione di copertura</Label>
                <Select value={opzioneTipo} onValueChange={setOpzioneTipo}>
                  <SelectTrigger data-testid="mrv-opzione" className="[&>span]:truncate"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.opzioni_tipo.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Combinazione garanzie</Label>
                <Select value={combinazione} onValueChange={setCombinazione}>
                  <SelectTrigger data-testid="mrv-combinazione" className="[&>span]:truncate"><SelectValue /></SelectTrigger>
                  <SelectContent>{combos.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Numero di volontari</Label>
                  <Input data-testid="mrv-volontari" type="number" min={config.min_volontari} value={nVolontari}
                    onChange={(e) => setNVolontari(e.target.value)} placeholder={`min ${config.min_volontari}`} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data di effetto</Label>
                  <Input data-testid="mrv-data-effetto" type="date" value={dataEffetto} onChange={(e) => setDataEffetto(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-border/60 pt-4">
              <SinistriDeclaration value={sinistri} onChange={setSinistri}
                periodoAnni={SINISTRI_YEARS.mrv_volontariato} testidPrefix="mrv-sinistri" />
              <div className="mt-4 border-t border-border/60 pt-4">
                <p className="mb-2 text-sm font-semibold text-forest">Valutazione di adeguatezza (IDD)</p>
                <IddDeclaration value={idd} onChange={setIdd} testidPrefix="mrv-idd" />
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card className="sticky top-24 rounded-xl border-border/60 p-5">
            <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Calcolo premio</h2>
            <Button data-testid="mrv-calculate-button" onClick={() => calculate()} disabled={calculating || !nVolontari}
              className="h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
              {calculating ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Calculator className="h-5 w-5" /> Calcola premio</>}
            </Button>

            {result && (
              <div className="mt-5 space-y-4" data-testid="mrv-quote-result">
                {statusBanner && (
                  <div className={`flex gap-2 rounded-lg border p-3 text-sm ${statusBanner.tone}`}>
                    <statusBanner.icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-medium">{statusBanner.title}</div>
                      {statusBanner.msgs.map((m, i) => <div key={i} className="mt-0.5 text-xs opacity-90">{m}</div>)}
                    </div>
                  </div>
                )}

                {premium && (
                  <div className="rounded-lg bg-sage/50 p-4">
                    {Object.entries(premium.breakdown).map(([k, b]) => (
                      <div key={k} className="flex justify-between py-1 text-sm">
                        <span className="text-muted-foreground">{labels[k] || k}</span>
                        <span className="font-medium text-forest">{eur(b.gross)}</span>
                      </div>
                    ))}
                    <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2">
                      <span className="font-medium text-forest">Premio annuo applicato</span>
                      <span data-testid="mrv-premio-finale" className="font-heading text-2xl font-semibold text-forest">{eur(premium.gross_total)}</span>
                    </div>
                    {premium.tariff_gross_total != null && premium.tariff_gross_total !== premium.gross_total && (
                      <div className="text-right text-xs text-muted-foreground">da tariffa: {eur(premium.tariff_gross_total)}</div>
                    )}
                    {premium.minimum_premium_applicable && (
                      <div className="mt-2 text-xs text-amber-800" data-testid="mrv-minimo-applicato">Tariffa inferiore al premio annuo minimo D998 della combinazione: premio base = minimo {eur(premium.minimum_premium_value)}{premium.premio_scelta === "maggiorato" ? `, applicato maggiorato ${eur(premium.gross_total)}` : ""}.</div>
                    )}
                    <div className="mt-3 rounded-lg border border-border bg-sage/60 p-2.5" data-testid="mrv-scelta-box">
                      <div className="text-xs text-forest">Quale premio vuoi applicare?</div>
                      <div className="mt-2 flex gap-2">
                        <button data-testid="mrv-scelta-tariffa" onClick={() => chooseScelta("tariffa")}
                          className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium ${premioScelta !== "maggiorato" ? "border-forest bg-forest text-white" : "border-border text-forest/70"}`}>
                          {premium.minimum_premium_applicable ? "Minimo D998" : "Da tariffa"} {eur(premium.premio_base)}
                        </button>
                        <button data-testid="mrv-scelta-maggiorato" onClick={() => setPremioScelta("maggiorato")}
                          className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium ${premioScelta === "maggiorato" ? "border-forest bg-forest text-white" : "border-border text-forest/70"}`}>
                          Maggiorato (max {eur(premium.premio_massimo)})
                        </button>
                      </div>
                      {premioScelta === "maggiorato" && (
                        <div className="mt-2 flex gap-2">
                          <input type="number" min={premium.premio_base} max={premium.premio_massimo} value={premioMagg} onChange={(e) => setPremioMagg(e.target.value)}
                            placeholder={`Da ${premium.premio_base} a ${premium.premio_massimo}`} data-testid="mrv-premio-maggiorato"
                            className="w-full rounded-lg border border-input bg-white px-2 py-1.5 text-sm" />
                          <button onClick={() => calculate("maggiorato")} data-testid="mrv-applica-maggiorato" className="rounded-lg bg-terracotta px-3 text-xs font-semibold text-white">Applica</button>
                        </div>
                      )}
                    </div>
                    {premium.rateo && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Rateo al {premium.rateo.scadenza_rateo?.split("-").reverse().join("/")} ({premium.rateo.giorni}/360 gg, minimo € 50): <span className="font-medium text-forest">{eur(premium.rateo.importo)}</span>
                      </div>
                    )}
                  </div>
                )}

                <MrvConfronto rows={result.confronto} onPick={pickCombo} disabled={calculating} />

                {result.provvigione && (
                  <div data-testid="mrv-commission-box" className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-4">
                    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-terracotta">
                      <Wallet className="h-3.5 w-3.5" /> Provvigione (solo per te)
                    </div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{result.provvigione.pct}% dell'imponibile</span>
                      <span className="font-heading text-xl font-semibold text-terracotta">{eur(result.provvigione.importo)}</span>
                    </div>
                  </div>
                )}

                {result.status !== "non_ammissibile" && (
                  <Button data-testid="mrv-save-quote-button" onClick={save} disabled={saving || !anagraficaId}
                    className="h-12 w-full gap-2 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4" /> Salva preventivo</>}
                  </Button>
                )}
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <Lock className="h-3 w-3" /> Le provvigioni non compaiono mai nella vista cliente.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
